import {
    AddPointCustomization,
    AddPointCustomizationComponent,
    AddPointFieldCustomization,
    ConnectedAccountStatus,
    CustomFieldCustomization,
    CustomFieldUrlType,
    EmailType,
    EventTargetType,
    FeatureType,
    GetMessageResponse,
    GetOrganizationProductResponse,
    LogicFieldType,
    MultiSelectType,
    NumberType,
    OrganizationPurchaseHistory,
    PaymentTargetType,
    PhoneType,
    PopUpTypeOld,
    RatingType,
    SettingStructure,
    SettingsChangeEvent,
    SingleSelectType,
    TextType,
    TopicCategory,
    URLType,
    VisibilityField,
    VisibilityRule,
} from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, isDefined, undefinedIfEmpty } from '../../../helpers/utils';

import { Accordion } from '@vaadin/vaadin-accordion';
import { CreateMessageComponent } from '../../create-message-component';
import { CreateProductComponent } from '../../create-product-component';
import { Dialog } from '@material/mwc-dialog';
import { FormVisibilityComponent } from '../../form-visibility-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import featuresInfo from '../../../helpers/state/features-info';
import { getMainPage } from '../../../helpers/page-name';
import { getOrganizationMessages } from '../../../helpers/api/messages-api';
import { getOrganizationProducts } from '../../../helpers/api/products-api';
import { getOrganizationPurchaseHistory } from '../../../helpers/api/purchases-api';
import { loadStripeStatus } from '../../../helpers/api/stripe-api';
import organizationInfo from '../../../helpers/state/organization-info';
import pointCache from '../../../helpers/state/point-cache';
import { standardFields } from '../../../helpers/constants';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-collab-settings-addon')
export class AdminCollabSettingsAddon extends LitElement {
    amplitude_callsite: string = 'Collaboration Settings Drawer';

    @query('#accordion')
    accordionElement?: Accordion;

    @query('#fieldType')
    fieldTypeElement?: HTMLSelectElement;

    @query('#customFieldName')
    customFieldNameElement?: HTMLInputElement;

    @query('#createMessageComponent')
    public createMessageComponent?: CreateMessageComponent;

    @query('#createProductComponent')
    createProductComponent?: CreateProductComponent;

    @query('#popUp')
    previewPopUpElement?: Dialog;

    @property({ type: Array })
    addPointCustomizationObject: AddPointCustomization[] = [];

    @state()
    defaultCustomizationElements: AddPointCustomizationComponent[] = [];

    @state()
    customCustomizationElements: AddPointCustomizationComponent[] = [];

    @state()
    formCustomizationElements: AddPointCustomizationComponent[] = [];

    @state()
    customFormElements: CustomFieldCustomization[] = [];

    @state()
    submissionCustomizationElements: SettingStructure[] = [];

    @state()
    postSubmissionCustomizationElements: SettingStructure[] = [];

    @state()
    customIterator: number = 0;

    @state()
    addingNewField: boolean = false;

    @state()
    rerenderingCustomOrder: boolean = false;

    @state()
    needsSave = false;

    @state()
    showPreviewForm: boolean = false;

    @state()
    showPreviewPointCard: boolean = false;

    @state()
    previewPointIndex: number = 0;

    @state()
    selected_tab: string = 'Custom Form Fields & Spreadsheet Columns';

    @state()
    pointMessages: GetMessageResponse[] = [];

    @state()
    visibilityTargetOptions: VisibilityField[] = [];

    @state()
    formProducts: GetOrganizationProductResponse[] = [];

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    stripeInfo: ConnectedAccountStatus | undefined;

    @state()
    isSaving: boolean = false;

    options: string[] = ['Custom Form Fields & Spreadsheet Columns', 'Submission Settings',  'Pending Submissions', 'Messages', 'Payment'];

    textType: TextType = {Text: {}};
    numberType: NumberType = { Number: { max: 100, min: 1 } };
    phoneType: PhoneType = { Phone: { maxLength: 10, minLength: 1 } };
    emailType: EmailType = { Email: { maxLength: 100, minLength: 1 } };
    urlType: URLType = { URL: { maxLength: 100, minLength: 1, limitToDomain: false, domains: [] } };
    multiSelectType: MultiSelectType = { MultiSelect: { options: ['Option1', 'Option2'] } };
    singleSelectType: SingleSelectType = { SingleSelect: { options: ['Option1', 'Option2'] } };
    ratingType: RatingType = { Rating: { maxStars: 5 } };

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // wait until the topicData is ready
        this.defaultCustomizationElements = [ // if you re-order these, make sure the order matches the save

            {
                customization: 'location',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Location',
                hasHideOnForm: false,
                hasTitle: true,
                hasDescription: true,
                hasRequired: false,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.location?.header ?? '',
                description: topicData.topic?.add_point_customization?.location?.sub_header ?? '',
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.location?.hide_on_point_card ?? false,
                icon: 'location_on',
                hasVisibilityRules: false,
                visibilityRules: topicData.topic?.add_point_customization?.location?.visibility_rules,
            },
            {
                customization: 'category',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Category',
                hasHideOnForm: false,
                hasTitle: true,
                hasDescription: true,
                hasRequired: false,
                hasHideOnPointCard: false,
                title: topicData.topic?.add_point_customization?.category?.header ?? '',
                description: topicData.topic?.add_point_customization?.category?.sub_header ?? '',
                icon: 'category',
                hasVisibilityRules: false,
                visibilityRules: topicData.topic?.add_point_customization?.category?.visibility_rules,
            },
            {
                customization: 'tags',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Tags',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.tags?.header ?? '',
                description: topicData.topic?.add_point_customization?.tags?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.tags?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.tags?.hide_on_point_card ?? false,
                required: topicData.topic?.add_point_customization?.tags?.mandatory ?? false,
                icon: 'local_offer',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.tags?.visibility_rules,
            },
            {
                customization: 'title',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Title',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.title?.header ?? '',
                description: topicData.topic?.add_point_customization?.title?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.title?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.title?.hide_on_point_card ?? false,
                required: topicData.topic?.add_point_customization?.title?.mandatory ?? false,
                icon: 'title',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.title?.visibility_rules,
            },
            {
                customization: 'description',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Description',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.description?.header ?? '',
                description: topicData.topic?.add_point_customization?.description?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.description?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.description?.hide_on_point_card ?? false,
                required: topicData.topic?.add_point_customization?.description?.mandatory ?? false,
                icon: 'description',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.description?.visibility_rules,
            },
            {
                customization: 'more_info',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: `${topicData.topic?.text_more_info ?? 'More Info'} URL`,
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: false,
                title: topicData.topic?.add_point_customization?.more_info?.header ?? '',
                description: topicData.topic?.add_point_customization?.more_info?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.more_info?.hide ?? false,
                required: topicData.topic?.add_point_customization?.more_info?.mandatory ?? false,
                icon: 'link',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.more_info?.visibility_rules,
            },
            {
                customization: 'special_offer',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Special Offer',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.special_offer?.header ?? '',
                description:
                    topicData.topic?.add_point_customization?.special_offer?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.special_offer?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.special_offer?.hide_on_point_card ?? false,
                required: topicData.topic?.add_point_customization?.special_offer?.mandatory ?? false,
                icon: 'star',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.special_offer?.visibility_rules,
            },
            {
                customization: 'photos',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Photos',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: false,
                hasHideOnPointCard: false,
                title: topicData.topic?.add_point_customization?.photos?.header ?? '',
                description: topicData.topic?.add_point_customization?.photos?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.photos?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.photos?.hide_on_point_card ?? false,
                icon: 'photo',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.photos?.visibility_rules,
            },
            {
                customization: 'date_time',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Date/Time',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: false,
                hasHideOnPointCard: true,
                title: topicData.topic?.add_point_customization?.date_time?.header ?? '',
                description: topicData.topic?.add_point_customization?.date_time?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.date_time?.hide ?? false,
                hideOnPointCard:
                    topicData.topic?.add_point_customization?.date_time?.hide_on_point_card ?? false,
                icon: 'schedule',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.date_time?.visibility_rules,
            },
        ];

        this.formCustomizationElements = [
            {
                customization: 'email',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Submitter Email Input',
                hasHideOnForm: true,
                hasTitle: true,
                hasDescription: true,
                hasRequired: true,
                hasHideOnPointCard: false,
                title: topicData.topic?.add_point_customization?.email?.header ?? '',
                description: topicData.topic?.add_point_customization?.email?.sub_header ?? '',
                hideOnForm: topicData.topic?.add_point_customization?.email?.hide ?? false,
                required: topicData.topic?.add_point_customization?.email?.mandatory ?? true,
                icon: 'email',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.email?.visibility_rules,
            },
            {
                customization: 'terms',
                userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                componentTitle: 'Terms and Conditions',
                hasHideOnForm: false,
                hasTitle: false,
                hasDescription: true,
                hasRequired: false,
                hasHideOnPointCard: false,
                description: topicData.topic?.add_point_customization?.terms?.header ?? '', // this one is handled differently
                icon: 'check_box',
                hasVisibilityRules: true,
                visibilityRules: topicData.topic?.add_point_customization?.terms?.visibility_rules,
            }
        ];

        // iterate through TopicData.topic.
        this.customCustomizationElements = [];
        topicData.topic?.add_point_customization?.custom_fields?.forEach((customField) => {
            this.customIterator++;
            const  icon =  'Text' in customField.field_type ? 'short_text' :
                'Number' in customField.field_type ? '123' :
                    'Phone' in customField.field_type ? 'phone' :
                        'URL' in customField.field_type ? 'link' :
                            'MultiSelect' in customField.field_type ? 'checklist' :
                                'Email' in customField.field_type ? 'email' :
                                    'SingleSelect' in customField.field_type ? 'radio_button_checked' :
                                        'Rating' in customField.field_type ? 'star' :
                                            'text';

            if (!customField.archived) {
                this.customCustomizationElements.push({
                    customization: `custom_${customField._id?.$oid ?? this.customIterator}`,
                    customFieldId: customField._id,
                    userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                    componentTitle: customField.customizations.header ?? 'Custom Field',
                    hasHideOnForm: true,
                    hasTitle: true,
                    hasDescription: true,
                    hasRequired: true,
                    hasHideOnPointCard: true,
                    title: customField.customizations.header,
                    description: customField.customizations.sub_header,
                    hideOnForm: customField.customizations.hide ?? false,
                    hideOnPointCard: customField.customizations.hide_on_point_card ?? false,
                    required: customField.customizations.mandatory ?? false,
                    customType: customField.field_type,
                    archived: customField.archived ?? false,
                    icon,
                    hasVisibilityRules: true,
                    visibilityRules: customField.customizations.visibility_rules
                });
            }
        });


        this.submissionCustomizationElements = [
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                title: 'Ask for Email First',
                fieldName: 'email_first',
                description: `Ask for the user's email address, if we don't have it yet, before they can submit a point, instead of at the end of the form.`,
                initialValueBoolean: topicData.topic?.add_point_customization?.email_first ?? false,
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                subfields: ['verify_email'],
                isSubfield: false,
                isHidden: false,
                flippedLanguage: false,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                title: 'Require Email Verification',
                fieldName: 'verify_email',
                description: 'Require the user to verify their email address using a six-digit code before they can submit a point. They will only be asked to do this once.',
                initialValueBoolean: topicData.topic?.add_point_customization?.verify_email ?? false,
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: true,
                isHidden: true,
                flippedLanguage: false,
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                title: 'Custom Email Notification',
                fieldName: 'custom_email_warning',
                description: 'Customize the message that appears at the end of the form, notifying users that they will receive an email with a secret link to edit their point.',
                initialValueString: topicData.topic?.add_point_customization?.custom_email_warning ?? '',
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
            },
        ];

        this.postSubmissionCustomizationElements = [
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                title: 'Display Confetti on Submission',
                fieldName: 'suppress_confetti',
                description: 'Show the confetti animation that appears when a user submits a point.',
                initialValueBoolean: !(topicData.topic?.add_point_customization?.suppress_confetti ?? true),
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
                flippedLanguage: true,
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                title: 'Custom Success Message',
                fieldName: 'custom_success_message',
                description: 'Customize the message that appears after a user submits a point.',
                initialValueString: topicData.topic?.add_point_customization?.custom_success_message ?? '',
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                title: 'Show Add Another Button',
                fieldName: 'show_add_another',
                description: 'Encourage users to submit multiple points by showing an "Add Another" button after they submit a point.',
                initialValueBoolean: topicData.topic?.add_point_customization?.show_add_another ?? true,
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
                flippedLanguage: false,
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                title: 'Custom Email Subject',
                fieldName: 'custom_email_subject',
                description: 'Customize the subject line of the email that is sent to users after they submit a point.',
                initialValueString: topicData.topic?.add_point_customization?.custom_email_subject ?? '',
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                title: 'Custom Email Message',
                fieldName: 'custom_email_message',
                description: 'Customize the message that is sent to users after they submit a point.',
                initialValueString: topicData.topic?.add_point_customization?.custom_email_message ?? '',
                isTopic: false,
                hasEntitlement: featuresInfo.customizeAddPointFormEnabled,
                featureType: FeatureType.CustomizeAddPointForm,
                isSubfield: false,
                isHidden: false,
            },
        ];

        this.customFormElements = topicData.topic?.add_point_customization?.custom_fields ?? [];

        this.getVisibilityOptions();
        await this.getPointMessages();
        await this.getFormProducts();
    }

    async onChange(e: CustomEvent <SettingsChangeEvent>) {
        const updatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: updatedValue,
        }, 'customize add point add on');
    }

    async onOtherChange(e: CustomEvent) {
        const updatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        if (topicData.topic?.add_point_customization === undefined) {
            const empty_customisation: AddPointCustomization = {
            };

            await topicData.fetchAndUpdateTopicData({
                add_point_customization: empty_customisation,
            }, 'customize add point add on');
        }

        if (topicData.topic?.add_point_customization) {
            const addPointCustomization = topicData.topic.add_point_customization;
            if (fieldToUpdate === 'email_first') {
                addPointCustomization.email_first = updatedValue;
            } else if (fieldToUpdate === 'verify_email') {
                addPointCustomization.verify_email = updatedValue;
            } else if (fieldToUpdate === 'custom_success_message') {
                addPointCustomization.custom_success_message = updatedValue;
            } else if (fieldToUpdate === 'show_add_another') {
                addPointCustomization.show_add_another = updatedValue;
            } else if (fieldToUpdate === 'custom_email_warning') {
                addPointCustomization.custom_email_warning = updatedValue;
            } else if (fieldToUpdate === 'custom_email_subject') {
                addPointCustomization.custom_email_subject = updatedValue;
            } else if (fieldToUpdate === 'custom_email_message') {
                addPointCustomization.custom_email_message = updatedValue;
            } else if (fieldToUpdate === 'suppress_confetti') {
                addPointCustomization.suppress_confetti = updatedValue; // this is a toggle
            }

            await topicData.fetchAndUpdateTopicData({
                add_point_customization: addPointCustomization,
            }, 'customize add point add on');
        }
    }

    async onAddPointChange(e: CustomEvent, customizationElements: AddPointCustomizationComponent[]) {
        // get field and sub field name with value and push into cusomizationElements
        const fieldToUpdate = e.detail.field as keyof AddPointCustomization;
        const subFieldToUpdate = e.detail.subField as keyof AddPointFieldCustomization;
        const updatedValue = e.detail.updatedValue;

        const fieldIndex = customizationElements.findIndex(
            (element) => element.customization === fieldToUpdate
        );

        if (subFieldToUpdate === 'hide_on_point_card') {
            customizationElements[fieldIndex].hideOnPointCard = updatedValue;
        } else if (subFieldToUpdate === 'hide') {
            customizationElements[fieldIndex].hideOnForm = updatedValue;
        } else if (subFieldToUpdate === 'mandatory') {
            customizationElements[fieldIndex].required = updatedValue;
        } else if (subFieldToUpdate === 'header') {
            customizationElements[fieldIndex].title = updatedValue;
        } else if (subFieldToUpdate === 'sub_header') {
            customizationElements[fieldIndex].description = updatedValue;
        }

        await this.save();
        this.requestUpdate();
    }

    async setEditedMessage(e: CustomEvent) {
        const message = e.detail.message;
        if (this.createMessageComponent) {
            await this.createMessageComponent.setExistingMessage(message);
        }
    }

    async setEditedProduct(e: CustomEvent) {
        const product = e.detail.product;
        if (this.createProductComponent) {
            await this.createProductComponent.setExistingProduct(product);
        }
    }

    // includes a necessary hack to fully rerender the component
    async moveUp(index: number) {
        this.rerenderingCustomOrder = true;
        this.requestUpdate();
        await delay(1000);
        [this.customCustomizationElements[index - 1], this.customCustomizationElements[index]] = [this.customCustomizationElements[index], this.customCustomizationElements[index - 1]];
        this.rerenderingCustomOrder = false;
        await this.save();
        this.requestUpdate();
    }

    async moveDown(index: number) {
        this.rerenderingCustomOrder = true;
        this.requestUpdate();
        await delay(1000);
        [this.customCustomizationElements[index], this.customCustomizationElements[index + 1]] = [this.customCustomizationElements[index + 1], this.customCustomizationElements[index]];
        this.rerenderingCustomOrder = false;
        await this.save();
        this.requestUpdate();
    }

    async onChangeCrowdsourceSetting(e: CustomEvent) {
        const category: TopicCategory = e.detail.category;
        const canCrowdsource = e.detail.canCrowdsource;

        const categories = topicData.categories;

        // find the category in the topicData using the category id
        if (categories !== undefined) {
            const categoryIndex = categories.findIndex(
                (element) => element._id === category._id
            );
            if (categoryIndex !== -1) {
                categories[categoryIndex].can_crowdsource = canCrowdsource;
            }
        }

        await topicData.fetchAndUpdateTopicData({
            categories,
        }, 'crowdsource setting');
    }

    async onChangeTagCrowdsourceSetting(e: CustomEvent) {
        const tag = e.detail.tag;
        const canCrowdsource = e.detail.canCrowdsource;

        const tags = topicData.allUserTags;

        // find the tag in the topicData using the tag id
        const new_tag = tags.find((element) => element._id?.$oid === tag._id.$oid);
        if (new_tag) {
            new_tag.can_crowdsource = canCrowdsource;
            const isTopLevelTag = topicData.topLevelUserTags.find(
                (element) => element._id?.$oid === tag._id.$oid
            );

            if (isTopLevelTag) {
                await topicData.fetchAndUpdateUserTag(new_tag?._id?.$oid ?? '', new_tag, 'crowdsource setting');
            } else {
                const partent_tag = topicData.topLevelUserTags.find(
                    (element) => element.tags?.find((subTag) => subTag._id?.$oid === tag._id.$oid)
                );
                if (partent_tag) {
                    await topicData.fetchAndUpdateUserSubTag(partent_tag._id?.$oid ?? '', new_tag._id?.$oid ?? '', new_tag, 'crowdsource setting');
                }
            }
        }
    }

    async onChangeURLButtonSettings(setting: AddPointCustomizationComponent, e: CustomEvent) {
        const updatedValues = e.detail as Partial<CustomFieldUrlType>;

        if (setting.customType && 'URL' in setting.customType) {
            const urlType = setting.customType.URL;
            setting.customType = {
                URL: {
                    ...urlType,
                    ...updatedValues,
                },
            };
            // update the customType in the topicData
            await this.save();
        }
    }

    async onChangeRatingSettings(setting: AddPointCustomizationComponent, e: CustomEvent) {
        const updatedValues = e.detail as Partial<{ maxStars: number }>;

        if (setting.customType && 'Rating' in setting.customType) {
            const ratingType = setting.customType.Rating;
            setting.customType = {
                Rating: {
                    ...ratingType,
                    ...updatedValues,
                },
            };
            await this.save();
        }
    }

    openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.CustomizeAddPointForm
                }
            })
        );
    }

    async addCustomField() {
        let customType;
        let icon;
        switch (this.fieldTypeElement?.value) {
            case 'Text':
                customType = this.textType;
                icon = 'short_text';
                break;
            case 'Number':
                customType = this.numberType;
                icon = '123';
                break;
            case 'Phone':
                customType = this.phoneType;
                icon = 'phone';
                break;
            case 'Email':
                customType = this.emailType;
                icon = 'email';
                break;
            case 'URL':
                customType = this.urlType;
                icon = 'link';
                break;
            case 'MultiSelect':
                customType = this.multiSelectType;
                icon = 'checklist';
                break;
            case 'SingleSelect':
                customType = this.singleSelectType;
                icon = 'radio_button_checked';
                break;
            case 'Rating':
                customType = this.ratingType;
                icon = 'star';
                break;
        }

        this.customIterator++;
        this.customCustomizationElements.push({
            customization: `custom_${this.customIterator}`,
            userHasEntitlement: featuresInfo.customizeAddPointFormEnabled,
            componentTitle: this.customFieldNameElement?.value ?? 'Custom Field',
            hasHideOnForm: true,
            hasTitle: true,
            hasDescription: true,
            hasRequired: true,
            hasHideOnPointCard: true,
            title: this.customFieldNameElement?.value ?? 'Custom Field',
            description: '',
            hideOnForm: false,
            hideOnPointCard: !!(customType === this.emailType || customType === this.phoneType),
            required: false,
            customType,
            archived: false,
            icon
        });

        this.addingNewField = false;

        await this.save();
    }

    openCustomFieldQuestions() {
        this.addingNewField = true;
    }

    private getVisibilityOptions() {
       this.visibilityTargetOptions = standardFields;
       // find the one with the name category and add the optiosn there
        const categoryField = this.visibilityTargetOptions.find(field => field.name === 'Category');
        if (categoryField) {
            categoryField.options = topicData.categories.map(category => {
                return {
                    value: category._id?.$oid ?? '',
                    name: category.name
                };
            }
        );
        }
        // add tag options
        const tagField = this.visibilityTargetOptions.find(field => field.name === 'Tags');
        if (tagField && topicData.allUserTags.length > 0) {
            tagField.options = topicData.allUserTags.map(tag => {
                return {
                    value: tag._id?.$oid ?? '',
                    name: tag.name
                };
            }
        );
        }

        const customVisibilityOptions = (topicData.topic?.add_point_customization?.custom_fields ?? []).map(field => {
            let type: string;
            if ('MultiSelect' in field.field_type || 'SingleSelect' in field.field_type) {
                type = 'select';
            } else if ('Number' in field.field_type) {
                type = 'number';
            } else {
                type = 'text';
            }
            return {
                value: field._id?.$oid ?? '',
                name: field.customizations.header ?? 'Custom Field',
                formPart: LogicFieldType.CustomField,
                inputType: type as 'text' | 'select' | 'number',
                options: 'MultiSelect' in field.field_type && isDefined(field.field_type.MultiSelect)  ? field.field_type.MultiSelect.options.map(option => {
                    return {
                        value: option,
                        name: option
                    };
                }
                ) : 'SingleSelect' in field.field_type && isDefined(field.field_type.SingleSelect) ? field.field_type.SingleSelect.options.map(option => {
                    return {
                        value: option,
                        name: option
                    };
                }
                ) : []
                };
            }
        );

        // Remove existing options that match the new ones
        this.visibilityTargetOptions = this.visibilityTargetOptions.filter(targetOption =>
            !customVisibilityOptions.some(customOption => customOption.value === targetOption.value)
        );

        // Add the new options
        this.visibilityTargetOptions.push(...customVisibilityOptions);
        this.needsSave = true;
    }

    private async processNewOptions(setting: AddPointCustomizationComponent, options_string: string) {
        const options = options_string.split(',').map((option) => option.trim()) ?? [];
        await this.updateOptions(setting, options);
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        void delay(400);
        // find title in settingsList as either fieldnametoggle or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find((setting) => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
            setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
        this.requestUpdate();
    }


    async getPointMessages() {
        try {
            this.pointMessages = await getOrganizationMessages(EventTargetType.Form, topicData.topicId, topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load point messages:', error);
            getMainPage().errorToastElement.showToast('Failed to load point messages.');
            this.pointMessages = [];
        }
    }

    async getFormProducts() {
        try {
            this.formProducts = await getOrganizationProducts(PaymentTargetType.Form, topicData.topicId, topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load form products:', error);
            getMainPage().errorToastElement.showToast('Failed to load form products.');
            this.formProducts = [];
        }
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];

        if (this.selected_tab === 'Payment' && organizationInfo.organization?.is_administrator === true) {
            void this.loadPaymentData();
        }
    }

    private async loadPaymentData() {
        if (!topicData.topic?.organization_id || !isDefined(organizationInfo.organization?._id)) {
            console.error('No organization ID found for topic.');
            return;
        }
        try {
            [this.stripeInfo, this.purchaseHistory] = await Promise.all([
                loadStripeStatus(organizationInfo.organization?._id.$oid ?? ''),
                getOrganizationPurchaseHistory(organizationInfo.organization?._id.$oid ?? '', topicData.topicId)
            ]);
        } catch (error) {
            getMainPage().errorToastElement.showToast('Failed to load purchases information.');
        }
    }

    private async addNewVisibilityRule(customization: string): Promise<void> {
        const visibilityComponent = this.shadowRoot?.querySelector(`#formVisibilityComponent_${customization}`) as FormVisibilityComponent;

        if (isDefined(visibilityComponent) && typeof visibilityComponent.addAndRule === 'function') {
            await visibilityComponent.addAndRule();
            this.requestUpdate();
        }
    }


    renderCustomizableFields(customizationElements: AddPointCustomizationComponent[], isCustom: boolean = false) {
        return html`
            ${customizationElements.map((setting, index) => {
                const customization_value = setting.customFieldId?.$oid ?? setting.customization;
                return html`
                <vaadin-accordion-panel theme="small" class="wide" style="margin: 0;
                    border: solid 1px var(--lumo-contrast-10pct);
                    border-radius: 2px;
                    padding: 5px;
                    margin-bottom: 5px">
                    <div slot="summary" class="summary" style="${setting.archived === true ? 'text-decoration: dashed' : ''}">
                        <mwc-icon slot="toggle" >${setting.icon}</mwc-icon>
                        <span class="sub-title">${setting.componentTitle}</span>
                        ${setting.hasHideOnForm ? html`
                            <span class="visibility-square"><mwc-icon>${setting.hideOnForm === true ? 'visibility_off' : 'visibility'}</mwc-icon> Form</span>
                        ` : ''}
                        ${setting.hasHideOnPointCard ? html`
                            <span class="visibility-square"><mwc-icon>${setting.hideOnPointCard === true  ? 'visibility_off' : 'visibility'}</mwc-icon> Card</span>
                        ` : ''}
                        <div class="move">
                            ${isCustom && customizationElements.length > 1 ? html`
                                ${index > 0 ? html`<mwc-icon class="move-icon" @click=${async (e: Event) => { e.stopPropagation(); await this.moveUp(index); }}>arrow_upward</mwc-icon>` :''}
                                ${index < customizationElements.length - 1 ? html`<mwc-icon  class="move-icon"  @click=${async (e: Event) => { e.stopPropagation(); await this.moveDown(index); }}>arrow_downward</mwc-icon>` : ''}
                            ` : ''}
                        </div>
                    </div>
                    <vaadin-vertical-layout>
                        <add-point-customization-element
                            .inputData=${setting}
                            id=${setting.customization}
                            @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer()}
                            @on-toggle-subfield=${async (e: CustomEvent) => await this.onAddPointChange(e, customizationElements)}
                            @update-crowdsource-setting=${async (e: CustomEvent) => await this.onChangeCrowdsourceSetting(e)}
                            @update-tag-crowdsource-setting=${async (e: CustomEvent) => await this.onChangeTagCrowdsourceSetting(e)}
                            @update-url-settings=${async (e: CustomEvent) => await this.onChangeURLButtonSettings(setting, e)}
                            @update-rating-settings=${async (e: CustomEvent) => await this.onChangeRatingSettings(setting, e)}
                            >
                        </add-point-customization-element>
                        ${setting.hideOnForm ?? false ? '' : html`
                            ${setting.customType && ('MultiSelect' in setting.customType || 'SingleSelect' in setting.customType)  ? html`
                                <div class="setOptions">
                                    <p class="helpSubeHeaderUnderline">Set Options</p>
                                    <proxi-textfield
                                        id="customFieldOptions"
                                        style="width: 100%; margin-top: 0.5rem; color: grey; height: 60px; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                                        outlined
                                        label="Options (Comma Separated)"
                                        value="${(setting.customType as MultiSelectType).MultiSelect?.options?.join(', ') ?? (setting.customType as SingleSelectType).SingleSelect?.options?.join(', ')}"
                                        @change=${async (e: Event) => await this.processNewOptions(setting, (e.target as HTMLInputElement).value)}
                                    ></proxi-textfield>
                                </div>
                            ` : ''}
                            ${setting.hasVisibilityRules !== false ? html`
                                <div class="visibilityOptions ${featuresInfo.customizeAddPointFormEnabled ? '' : 'disabled'}" @click=${featuresInfo.customizeAddPointFormEnabled ? '' : this.openUpgradeDrawer}>
                                    <p class="helpSubeHeaderUnderline">Logic</p>
                                        <p class="info-paragraph">The ${setting.customization.startsWith('custom') ? setting.title : undefinedIfEmpty(setting.title) ?? setting.customization} field will only appear on the form when: </p>
                                            <form-visibility-component
                                                id="formVisibilityComponent_${setting.customization}"
                                                style="${isDefined(setting.visibilityRules) && setting.visibilityRules.length > 0 ? '' : 'hidden'}"
                                                .visibilityRules=${setting.visibilityRules ?? []}
                                                .visibilityTargetOptions=${this.visibilityTargetOptions}
                                                .customFormElements=${this.customFormElements}
                                                .currentFieldValue=${customization_value}
                                                @visibility-rules-updated=${async (e: CustomEvent) => {
                                                    await this.updateVisibilityRules(e.detail.fieldName, e.detail.visibilityRules);
                                                    this.requestUpdate();
                                                }}
                                            ></form-visibility-component>
                                    ${!isDefined(setting.visibilityRules) || setting.visibilityRules?.length === 0 ? html`
                                        <br>
                                        <button class="add_visibility_rule" @click=${async () => await this.addNewVisibilityRule(setting.customization)}>
                                            Add Logic Rule
                                        </button>
                                    ` : html`
                                        <div class="add-new-rule-wrapper">
                                            <div class="operator-holder" @click=${async () => await this.addNewVisibilityRule(setting.customization)}>
                                                <span class="operator and clickable-and">
                                                    <mwc-icon>add_circle</mwc-icon> AND
                                                </span>
                                            </div>
                                        </div>
                                    `}
                                </div>
                            ` : ''}
                        `}
                        ${isDefined(setting.customType) && isDefined(setting.archived) ? html`
                            <div class="archiveField" @click=${async() => {
                                setting.archived = !(setting.archived ?? false);
                                if (setting.archived) {
                                    setting.hideOnPointCard = true;
                                    setting.hideOnForm = true;
                                    setting.required = false;
                                };
                                await this.save();
                                this.requestUpdate();
                            }}>
                                <span>${setting.archived ? 'Archiving...' : 'Delete Field'}</span>
                            </div>
                        ` : ''}
                    </vaadin-vertical-layout>
                </vaadin-accordion-panel>`;
            })}
        `;
    }

    renderMessageCenter() {
        return html`
            <settings-card header="Message Contacts (Beta)">
                <p class="info-paragraph">Send a custom message to all contacts or point adders. The email can include a link to their individual edit page.</p>
                    <create-message-component
                    id="createMessageComponent"
                        .message_target_type=${EventTargetType.Form}
                        .message_target_id=${topicData.topic?._id?.$oid ?? ''}
                        .featureEnabled=${featuresInfo.customizeAddPointFormEnabled}
                        @message-sent="${this.getPointMessages}"
                    >
                    </create-message-component>
                </settings-card>
                <settings-card header="Sent and Scheduled Messages">
                    <p class="info-paragraph">View and manage messages sent to contacts or point adders.</p>
                    ${this.pointMessages.length > 0 ? html`
                        <past-scheduled-message-component
                            .data="${this.pointMessages}"
                            .message_target_type=${EventTargetType.Form}
                            @set-edited-message="${this.setEditedMessage}"
                        >
                        </past-scheduled-message-component>
                    ` : html`
                        <p>No messages sent yet.</p>
                    `}
            </settings-card>
        `;
    }

    renderProductCenter() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <settings-card header="Payment Settings">
                    <p class="info-paragraph">You do not have the required permissions to view or manage products and purchases. Please contact your organization administrator for access.</p>
                </settings-card>
            `;
        }

        return html`
            <settings-card header="Create Products">
                <p class="info-paragraph">Create and manage products for contributions.</p>
                    <create-product-component
                    id="createProductComponent"
                        .payment_target_type=${PaymentTargetType.Form}
                        .stripeInfo=${this.stripeInfo}
                        .payment_target_entity_id=${topicData.topic?._id?.$oid ?? ''}
                        @product-updated="${this.getFormProducts}"
                    >
                </create-product-component>
            </settings-card>
            <settings-card header="Existing Products and Prices">
                <p class="info-paragraph">View and manage existing products and prices</p>
                ${this.formProducts.length > 0 ? html`
                    <existing-products-component
                        .data="${this.formProducts}"
                        .payment_target_type=${PaymentTargetType.Form}
                        .purchases=${this.purchaseHistory?.transactions ?? []}
                        @set-edited-product="${this.setEditedProduct}"
                        @update-products="${this.getFormProducts}"
                    >
                    </existing-products-component>
                ` : html`
                    <p>No products found.</p>
                `}
            </settings-card>
            <settings-card header="Completed Purchases">
                <p class="info-paragraph">View all completed purchases made by users.</p>
                ${!isDefined(this.purchaseHistory) || this.purchaseHistory.transactions.length > 0 ? html`
                    <completed-purchases-component
                        .data=${this.purchaseHistory}
                    ></completed-purchases-component>
                ` : html`
                    <p>No completed purchases found.</p>
                `}
            </settings-card>
        `;
    }

    render() {
        if (!topicData.isReady) {
            return html`<loading-element loadingString="Getting 'Best Of' Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <form-button
                            size="md"
                            variant="text"
                            icon="arrow_back"
                            @click=${() => this.dispatchEvent(new CustomEvent('close-fragment'))}
                        >Back</form-button>
                    </div>
                    <div class="middle-header-content">
                        Crowdsourcing & Field Customization Settings
                    </div>

                    <div class="right-header-content">
                        <map-header-buttons
                        .isCollabSettings=${true}
                            >
                        </map-header-buttons>
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.selected_tab === 'Submission Settings' ? 1 : this.selected_tab === 'Custom Form Fields & Spreadsheet Columns' ? 0 : this.selected_tab === 'Pending Submissions' ? 2 : this.selected_tab === 'Messages' ? 3 : this.selected_tab === 'Payment' ? 4 : 0}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Submission Settings' ? this.renderSettings() : ''}
                    ${this.selected_tab === 'Custom Form Fields & Spreadsheet Columns' ? this.renderFormFields() : ''}
                    ${this.selected_tab === 'Pending Submissions' ? this.renderCrowdsourcedPoints() : ''}
                    ${this.selected_tab === 'Messages' ? this.renderMessageCenter() : ''}
                    ${this.selected_tab === 'Payment' ? this.renderProductCenter() : ''}
                </div>
            </div>
                        <mwc-dialog
                id="electionModal"
                class="dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
        `;
    }

    protected renderSettings() {
        return html`
            <settings-card header="Approval Settings">
                <p class="info-paragraph">You can require approval for ${topicData.topic?.point_name ?? 'point'}s added to the map. When enabled, ${topicData.topic?.point_name ?? 'point'}s will not be visible on the map until they are approved by a moderator.</p>
                <point-approval></point-approval>
            </settings-card>
            <settings-card header="Submission Options">
                ${this.submissionCustomizationElements.map((setting, index) => {
                    return html`
                    <setting-component
                        style = 'width:$100%'
                        .setting_details=${setting}
                        .is_hidden=${setting.isHidden ?? false}
                        @on-change-value=${this.onOtherChange}
                        @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.submissionCustomizationElements)}
                        @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer()}
                        >
                    </setting-component>
                    `;
                })}
            </settings-card>
                        <settings-card header="Post Submission Message">
                ${this.postSubmissionCustomizationElements.map((setting, index) => {
                    return html`
                    <setting-component
                        style = 'width:$100%'
                        .setting_details=${setting}
                        .is_hidden=${setting.isHidden ?? false}
                        @on-change-value=${this.onOtherChange}
                        @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.submissionCustomizationElements)}
                        @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer()}
                        >
                    </setting-component>
                    `;
                })}
            </settings-card>
            <settings-card header="Post Submission Pop Up">
                <form-button
                    slot="header"
                    size="xs"
                    variant="outline"
                    icon="preview"
                    @click=${this.preview}
                >
                    Preview
                </form-button>
                <edit-pop-up-component
                    .pop_up_id="${topicData.postPointSubmissionPopUp?._id?.$oid}"
                    .pop_up_type=${PopUpTypeOld.PostPointSubmission}
                    .feature_type=${FeatureType.CustomizeAddPointForm}
                    .hasEntitlement=${featuresInfo.customizeAddPointFormEnabled}
                    @preview-pop-up=${this.preview}
                    >
                </edit-pop-up-component>
            </settings-card>
            <mwc-dialog id="popUp" class="dialog" hideActions>
                <modal-dialog @close=${this.closeMapPopUp} size="md">
                    <div slot="body">
                        <div id="popUpContent">
                            <email-capture-modal .popUp=${topicData.postPointSubmissionPopUp} class="inner_desktop_drawer_content" id="emailCapturePopUp" @close=${this.closeMapPopUp}></email-capture-modal>
                        </div>
                    </div>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    private async preview() {
        if (this.previewPopUpElement) {
            document.documentElement.style.setProperty('--mdc-theme-surface', topicData.postPointSubmissionPopUp?.background_color ?? '#FFF');
            this.style.setProperty('--mdc-theme-surface', topicData.postPointSubmissionPopUp?.background_color ?? '#FFF');
            this.previewPopUpElement.open = true;
        }
    }

    private async closeMapPopUp() {
        if (this.previewPopUpElement) {
            this.previewPopUpElement.open = false;
        }
    }

    protected renderFormFields() {
        // get point object with most characters
        return html`
            <div class="manage-participants-and-results">
                <div class="individual-participant-drawer ${this.showPreviewForm || this.showPreviewPointCard ? 'open' : 'closed'}">
                    <mwc-icon class="close-icon" style="margin-bottom: 10px;float: right;" @click="${() => {this.showPreviewPointCard = false; this.showPreviewForm = false;}}">
                        close
                    </mwc-icon>
                    <h3 class="sub-title"> You are previewing this element as it will appear to external users. </h3>
                    <hr>
                    ${this.showPreviewPointCard ? html`
                        <expanded-point-card
                            .discoveryView=${false}
                            .point=${pointCache.allPoints[this.previewPointIndex]}
                            .previewMode=${true}
                        >
                        </expanded-point-card>
                        ${this.showPreviewForm ? html`<hr>` : ''}
                    ` : ''}
                    ${this.showPreviewForm ? html`
                        <add-edit-point-component
                            id="addPointElement"
                            .editMode=${false}
                            .previewMode=${true}
                            hideTitle
                            bypassGoogleKey
                        ></add-edit-point-component>
                    ` : ''}
                </div>
                <div class="general-stats-area">
                    <settings-card header="Form Elements" maxWidth="${this.showPreviewForm || this.showPreviewPointCard ? '90%' : '50%'}" style="height: 100%; oveflow-y: auto;">
                        <div class="button-holder" slot="header" style="display: flex; gap: 5px;">
                            ${this.showPreviewForm ? '' : html`
                                <form-button size="xs" variant="outline" class="learn-more-button" icon="preview" @click="${() => {this.showPreviewForm = true; this.showPreviewPointCard = false;}}">
                                    Preview Form
                                </form-button>
                            `}
                            ${this.showPreviewPointCard ? '' : html`
                                <form-button slot="header" size="xs" variant="outline" class="learn-more-button" icon="preview" @click="${() => {this.showPreviewPointCard = true; this.showPreviewForm = false;}}">
                                    Preview ${topicData.topic?.point_name ?? 'Point'} Card
                                </form-button>
                            `}
                        </div>
                        ${this.renderOriginal()}
                    </settings-card>
                </div>
            </div>
        `;
    }

    renderCrowdsourcedPoints() {
        return html`
           <div class="general-stats-area">
            <crowdsourced-point-details
                style="flex:1;overflow:hidden;"
                >
            </crowdsourced-point-details>
              </div>
        `;
    }

    protected renderOriginal() {
        // TODO: Add QR Code element
        return html`
            <div id="holder" style="overflow-y: auto; height: 100%;">
                <div id="container">
                    <p class="info-paragraph">
                            Customize the form and spreadsheet structure where you, collaborators, and community members add ${topicData.topic?.point_name ?? 'point'}s to your map. Here you can update field names and descriptions in the Add ${topicData.topic?.point_name ?? 'Point'} Form to give
                            users custom instructions. You can also hide unwanted fields or make optional fields mandatory.</p><br><br>
                    <p>Default Elements</p>
                    <vaadin-accordion id="accordion" opened=${-1}>
                        ${this.renderCustomizableFields(this.defaultCustomizationElements)}
                    </vaadin-accordion>
                    <p style="display:inline-flex">Custom Elements
                    </p>
                    ${this.rerenderingCustomOrder ? html`
                        <loading-element loadingString="Updating Field Order" ?horizontal=${true}></loading-element>
                    ` : html`
                        <vaadin-accordion id="accordion" opened=${-1}>
                            ${this.renderCustomizableFields(this.customCustomizationElements, true)}
                        </vaadin-accordion>
                    `}
                    ${this.addingNewField ? html`
                        <div style="background-color: var(--theme-border-color); display:flex;flex-direction:column;gap:10px; width:100%; padding: 10px">
                            <span> Warning: You cannot change the field type after adding it to form. </span>
                            <proxi-selectfield
                                label="Field Type"
                                outlined
                                id="fieldType"
                                style=""
                            >
                                <mwc-list-item value="Text">Text</mwc-list-item>
                                <mwc-list-item value="Number">Number</mwc-list-item>
                                <mwc-list-item value="Phone">Phone Number</mwc-list-item>
                                <mwc-list-item value="Email">Email Address</mwc-list-item>
                                <mwc-list-item value="URL">URL</mwc-list-item>
                                <mwc-list-item value="MultiSelect">Multi-Select</mwc-list-item>
                                <mwc-list-item value="SingleSelect">Single Select</mwc-list-item>
                                <mwc-list-item value="Rating">Rating</mwc-list-item>
                            </proxi-selectfield>
                            <proxi-textfield
                                id="customFieldName"
                                outlined
                                label="Field Name"
                                value="New Custom Field"
                                style=""
                            ></proxi-textfield>
                            <button class="add_custom_field" @click=${this.addCustomField}>Add To Form</button>
                        ` : html `
                            ${featuresInfo.customizeAddPointFormEnabled ? html`
                                <button class="add_custom_field" @click=${this.openCustomFieldQuestions}>Add Custom Field</button>
                            ` : html`
                                <button class="add_custom_field" @click=${this.openUpgradeDrawer}>Add Custom Field</button>
                            `}
                        </div>
                    `}
                    <p>Submission Elements</p>
                    <vaadin-accordion id="accordion" opened=${-1}>
                        ${this.renderCustomizableFields(this.formCustomizationElements)}
                    </vaadin-accordion>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    async updateOptions(setting: AddPointCustomizationComponent, options: string[]) {
        if (setting.customType) {
            if ('MultiSelect' in setting.customType) {
                setting.customType.MultiSelect.options = options;
            } else if ('SingleSelect' in setting.customType) {
                setting.customType.SingleSelect.options = options;
            }
            await this.save();
        }
        this.requestUpdate();
    }

    async updateVisibilityRules(customization_value: string, visibilityRules: VisibilityRule[][]) {
        // find the setting in the array and update its visibility rules
        const allSettings = [...this.defaultCustomizationElements, ...this.formCustomizationElements, ...this.customCustomizationElements];
        const settingToUpdate = allSettings.find(s => s.customization === customization_value || (s.customFieldId?.$oid ?? '') === (customization_value ?? ''));
        if (settingToUpdate) {
            settingToUpdate.visibilityRules = visibilityRules;
            await this.save();
        }
        this.requestUpdate();
    }

    private async save() {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;
        const custom_fields: CustomFieldCustomization[] = this.customCustomizationElements.map((customField) => {
            return {
                _id: customField.customFieldId ?? undefined,
                customizations: {
                    header: customField.title,
                    sub_header: customField.description,
                    hide: customField.hideOnForm,
                    hide_on_point_card: customField.hideOnPointCard,
                    mandatory: customField.required,
                    visibility_rules: customField.visibilityRules
                },
                field_type: customField.customType ?? this.textType,
                archived: customField.archived ?? false
            };
        });

        const defaultMap = Object.fromEntries(this.defaultCustomizationElements.map((setting) => [setting.customization, setting]));
        const getMainField = (key: string) => defaultMap[key] ?? {};

        const formMap = Object.fromEntries(this.formCustomizationElements.map((setting) => [setting.customization, setting]));
        const getFormField = (key: string) => formMap[key] ?? {};

        const add_point_customization : AddPointCustomization = {
            location: {
                header: undefinedIfEmpty(getMainField('location').title),
                sub_header: undefinedIfEmpty(getMainField('location').description),
                hide_on_point_card: getMainField('location').hideOnPointCard ?? false,
                hide: getMainField('location').hideOnForm ?? false,
                mandatory: getMainField('location').required ?? false,
                visibility_rules: getMainField('location').visibilityRules
            },
            category: {
                header: undefinedIfEmpty(getMainField('category').title),
                sub_header: undefinedIfEmpty(getMainField('category').description),
                hide_on_point_card: getMainField('category').hideOnPointCard ?? false,
                hide: getMainField('category').hideOnForm ?? false,
                mandatory: getMainField('category').required ?? false,
                visibility_rules: getMainField('category').visibilityRules
            },
            tags: {
                header: undefinedIfEmpty(getMainField('tags').title),
                sub_header: undefinedIfEmpty(getMainField('tags').description),
                hide_on_point_card: getMainField('tags').hideOnPointCard ?? false,
                hide: getMainField('tags').hideOnForm ?? false,
                mandatory: getMainField('tags').required ?? false,
                visibility_rules: getMainField('tags').visibilityRules
            },
            title: {
                header: undefinedIfEmpty(getMainField('title').title),
                sub_header: undefinedIfEmpty(getMainField('title').description),
                hide_on_point_card: getMainField('title').hideOnPointCard ?? false,
                hide: getMainField('title').hideOnForm ?? false,
                mandatory: getMainField('title').required ?? false,
                visibility_rules: getMainField('title').visibilityRules
            },
            description: {
                header: undefinedIfEmpty(getMainField('description').title),
                sub_header: undefinedIfEmpty(getMainField('description').description),
                hide_on_point_card: getMainField('description').hideOnPointCard ?? false,
                hide: getMainField('description').hideOnForm ?? false,
                mandatory: getMainField('description').required ?? false,
                visibility_rules: getMainField('description').visibilityRules
            },
            more_info: {
                header: undefinedIfEmpty(getMainField('more_info').title),
                sub_header: undefinedIfEmpty(getMainField('more_info').description),
                hide_on_point_card: getMainField('more_info').hideOnPointCard ?? false,
                hide: getMainField('more_info').hideOnForm ?? false,
                mandatory: getMainField('more_info').required ?? false,
                visibility_rules: getMainField('more_info').visibilityRules
            },
            special_offer: {
                header: undefinedIfEmpty(getMainField('special_offer').title),
                sub_header: undefinedIfEmpty(getMainField('special_offer').description),
                hide_on_point_card: getMainField('special_offer').hideOnPointCard ?? false,
                hide: getMainField('special_offer').hideOnForm ?? false,
                mandatory: getMainField('special_offer').required ?? false,
                visibility_rules: getMainField('special_offer').visibilityRules
            },
            photos: {
                hide_on_point_card: getMainField('photos').hideOnPointCard ?? false,
                hide: getMainField('photos').hideOnForm ?? false,
                visibility_rules: getMainField('photos').visibilityRules,
                header: undefinedIfEmpty(getMainField('photos').title),
                sub_header: undefinedIfEmpty(getMainField('photos').description),
            },
            date_time: {
                header: undefinedIfEmpty(getMainField('date_time').title),
                sub_header: undefinedIfEmpty(getMainField('date_time').description),
                hide_on_point_card: getMainField('date_time').hideOnPointCard ?? false,
                hide: getMainField('date_time').hideOnForm ?? false,
                mandatory: getMainField('date_time').required ?? false,
                visibility_rules: getMainField('date_time').visibilityRules
            },
            email: {
                header: undefinedIfEmpty(getFormField('email').title),
                sub_header: undefinedIfEmpty(getFormField('email').description),
                hide_on_point_card: getFormField('email').hideOnPointCard ?? false,
                hide: getFormField('email').hideOnForm ?? false,
                mandatory: getFormField('email').required ?? true,
                visibility_rules: getFormField('email').visibilityRules
            },
            terms: {
                header: undefinedIfEmpty(getFormField('terms').description), // this one is handled differently
                visibility_rules: getFormField('terms').visibilityRules
            },
            custom_fields,
            email_first: topicData.topic?.add_point_customization?.email_first ?? false,
            verify_email: topicData.topic?.add_point_customization?.verify_email ?? false,
            custom_success_message: topicData.topic?.add_point_customization?.custom_success_message,
            suppress_confetti: topicData.topic?.add_point_customization?.suppress_confetti ?? false,
            custom_email_message: topicData.topic?.add_point_customization?.custom_email_message,
            custom_email_subject: topicData.topic?.add_point_customization?.custom_email_subject,
            custom_email_warning: topicData.topic?.add_point_customization?.custom_email_warning,
            show_add_another:  topicData.topic?.add_point_customization?.show_add_another ?? false,
        };

        await topicData.fetchAndUpdateTopicData({
            add_point_customization
        }, 'add_point_customization');

        await this.startup();
        this.isSaving = false;
    }

    static get styles() {
        return css`

            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            #container {
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                box-sizing: border-box;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
                margin-top: 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .gray {
                --mdc-theme-primary: var(--main-disabled-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .visibility-square {
                display: flex;
                margin-left: 0.5rem;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-color);
                border-radius: 5px;
                padding: 0.1rem 0.25rem;
                align-items: center;
                font-size: 0.8rem;
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
                display: flex;
                overflow-y: auto;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .helpSubHeader {
                display: inline-flex;
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
                align-items: center;
            }

            .summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .summary-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
                pointer-events: none;
            }

            .move {
                margin-left: 1rem;
            }

            .summary mwc-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 20px;
            }

            .move-icon {
                cursor: pointer;
                margin: 0;
            }

            .move-icon:hover {
                color: var(--main-primary-color);
            }

            .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            form-tooltip {
                font-size: 0.8rem;
            }

            .archiveField {
                cursor: pointer;
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                text-decoration: underline;
                color: var(--main-invalid-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .ruleSelectorWrapper {
                display: flex;
                margin-top: 1rem;
                flex-direction: row;
                flex-wrap: wrap;
                align-content: center;
                justify-content: center;
                gap: 3px 5px;
            }

            .gatedSection {
                cursor: pointer;
            }

            .description {
                color: var(--theme-input-color);
            }

            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .customizedFields__disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .hidden {
                display: none;
            }

            .ruleSubmissionButtons {
                display: flex;
                justify-content: center;
                margin-top: 5px;
                padding-bottom: 10px;
                gap: 5px;
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }
            }
        `;
    }
}
